import React, {useEffect, useState} from "react";
import Button from "../common/Button";
import CustomListbox, {Item} from "../common/CustomListbox";
import MainFilter from "./MainFilter";
import {useQuery} from "@tanstack/react-query";
import {fetchItem} from "../../data/ReactQueries";
import {enforceArray, handleChecklistFilterChange, replaceRouterQueryParamsShallow} from "../../helpers/FilterFunctions";
import {useRouter} from "next/router";
import {objects} from "../../models/objects";

import {cities} from "../../models/cities";
import {ParsedUrlQuery} from "querystring";
import {Filter, PropertyTypeQueryKey} from "../../data/Filter/Filter.config";
import {ArtDerUnterkunftItems} from "../../data/Filter/VermietungFilter.config";
import {useTranslation} from "next-i18next";
import IdSearchField from "../common/IdSearchField";

interface ImmobilieKaufenProps {
}

function ImmobilieKaufen(props: ImmobilieKaufenProps) {
	const router = useRouter();
	const {t} = useTranslation();

	const {data: activeCities, isLoading: activeCitiesLoading} = useQuery(
		["activeCities", {sale: 1}],
		(): Promise<cities[]> => {
			return fetchItem("cities", {sale: 1});
		},
		{initialData: []}
	);

	const [selectedCities, setSelectedCities] = useState<string[]>();
	const [selectedPropertyTypes, setSelectedPropertyTypes] =
		useState<string[]>();
	const [defaultSelectedCities, setDefaultSelectedCities] = useState<Item[]>();
	const [objectIdSearchVal, setObjectIdSearchVal] = useState<string>();
	const [defaultSelectedPropertyTypes, setDefaultSelectedPropertyTypes] =
		useState<Item[]>();
	useEffect(() => {
		if (activeCities.length > 0 && router.isReady) {
			if (router.query[Filter.City.queryKey]) {
				setDefaultSelectedCities(
					activeCities
						.filter((city) =>
							enforceArray(router.query[Filter.City.queryKey]).includes(
								city.id.toString()
							)
						)
						.map((city) => {
							return {name: city.name};
						})
				);
			}
			if (router.query[PropertyTypeQueryKey]) {
				setDefaultSelectedPropertyTypes(
					Object.values(Filter)
						.filter((filter) => {
							return (
								filter.values &&
								filter.queryKey === PropertyTypeQueryKey &&
								enforceArray(router.query[PropertyTypeQueryKey]).includes(
									filter.values[0]
								)
							);
						})
						.map((filter) => {
							return {name: filter.label};
						})
				);
			}
		}
	}, [router.isReady, activeCities]);

	function handleSearch(
		cities?: string[],
		propertyTypes?: string[],
		searchedObjectID?: string
		// priceRange?: number[]
	) {
		let queryParams = router.query;
		if (cities) {
			delete queryParams[Filter.City.queryKey];
			const cityIds = activeCities
				.filter((city) => cities.includes(city.name))
				.map((city) => city.id.toString());
			queryParams = {...queryParams, [Filter.City.queryKey]: cityIds};
		}
		if (propertyTypes) {
			delete queryParams[PropertyTypeQueryKey];
			const propertyQueryParams = propertyTypes
				.map((type) => {
					return handleChecklistFilterChange(type, true, router, true) ?? false;
				})
				.filter((i) => i !== false) as ParsedUrlQuery[];
			const flattend = propertyQueryParams
				.filter((i) => i.hasOwnProperty(PropertyTypeQueryKey))
				.map((i) => i[PropertyTypeQueryKey] as string[])
				.reduce((prev, item) => {
					if (item) {
						return [...prev, ...item];
					}
					return [];
				}, []);
			const deduplicated = flattend.filter((i, idx) => {
				return flattend.indexOf(i) === idx;
			});

			queryParams = {...queryParams, [PropertyTypeQueryKey]: deduplicated};
		}
		delete queryParams.id;
		if (searchedObjectID) {
			queryParams = {
				...queryParams,
				id: searchedObjectID
			};
		}
		// if (priceRange) {
		//   queryParams = {
		//     ...queryParams,
		//     [Filter.MinPrice.queryKey]: priceRange[0].toString(),
		//     [Filter.MaxPrice.queryKey]: priceRange[1].toString()
		//   };
		// }

		if (router.pathname != "/verkauf") {
			router.push({pathname: "/verkauf", query: queryParams});
		} else {
			replaceRouterQueryParamsShallow(router, queryParams);
		}
		if (typeof window !== "undefined") {
			setTimeout(() => {
				document
					.getElementById("search-result-container")
					?.scrollIntoView({behavior: "smooth"});
			}, 500);
		}
	}

	const submit = () => {
		handleSearch(
			selectedCities,
			selectedPropertyTypes,
			objectIdSearchVal
		);
	}
	return (
		<MainFilter>
			<div className="space-y-2 col-span-2 md:col-span-1 h-20">
        <span className="sm:text-[20px]">
          {t("components.immobilieKaufen.city")}
        </span>
				<CustomListbox
					items={enforceArray(activeCities).map((city) => {
						return {name: city.name};
					})}
					onSelectItems={(items) => {
						setSelectedCities(items.map((i) => i.name));
					}}
					defaultChecked={defaultSelectedCities}
				/>
			</div>
			<div className="space-y-2 col-span-2 md:col-span-1 h-20 pl-3 lg:pl-0">
        <span className="sm:text-[20px]">
          {t("components.immobilieKaufen.propertyType")}
        </span>
				<CustomListbox
					items={ArtDerUnterkunftItems(router)
						.items.map((i) => {
							return {name: i.primaryLabel};
						})
						.slice(1)}
					onSelectItems={(items) => {
						setSelectedPropertyTypes(items.map((i) => i.name));
					}}
					defaultChecked={defaultSelectedPropertyTypes}
				/>
			</div>
			<IdSearchField title={t("common.objectidlabel")} onChange={setObjectIdSearchVal} onSubmit={() => submit()}/>
			<div
				className="w-full col-span-2 lg:col-span-1 h-20 space-y-2 lg:place-self-end cursor-pointer"
				onClick={() => {
					submit();
				}}
			>
				<span className="text-[16px]">&nbsp;</span>
				<Button
					padding="px-0"
					text={t("components.searchField.search")}
					transparent={false}
					border={false}
					height={"h-[40px]"}
					className="rounded-[10px] font-bold"
					bgColor="bg-gradient-to-b from-[#a8e1f7] to-[#43c4f7]"
				/>
			</div>
		</MainFilter>
	);
}

export default ImmobilieKaufen;
